﻿import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import BlockRoundedIcon from "@mui/icons-material/BlockRounded"
import CorporateFareRoundedIcon from "@mui/icons-material/CorporateFareRounded"

import MainLayout from "../../layouts/MainLayout"

import {
  createStore,
  deleteStore,
  getNextStoreCode,
  getStoreById,
  getStoreFormData,
  getStores,
  updateStore,
} from "../../services/storeService"

import { getBusinessLabel } from "../../config/appConfig"


// =========================================================
// BUSINESS LABELS
// =========================================================

const MAIN_COMPANY = getBusinessLabel(
  "mainCompany",
  "Entreprise principale"
)

const MAIN_COMPANY_SHORT = getBusinessLabel(
  "mainCompanyShort",
  MAIN_COMPANY
)

const MAIN_DISTRIBUTOR = getBusinessLabel(
  "mainDistributor",
  "Distributeur principal"
)

const RELAY_DEPOT = getBusinessLabel(
  "relayDepot",
  "Structure secondaire"
)

const RELAY_DEPOTS = getBusinessLabel(
  "relayDepots",
  "Structures secondaires"
)

const SHOP = getBusinessLabel(
  "shop",
  "Shop"
)

const SHOPS = getBusinessLabel(
  "shops",
  "Shops"
)

const FIELD_AGENTS = getBusinessLabel(
  "fieldAgents",
  "Agents commerciaux"
)


// =========================================================
// CONSTANTS
// =========================================================

const ROWS_PER_PAGE = 10


const defaultFormData = {
  code: "",
  name: "",
  organization_id: "",
  parent_store_id: "",
  store_type: "kin_mousse_shop",
  address: "",
  city: "Kinshasa",
  province: "Kinshasa",
  is_active: true,
  company_id: 1,
}


const fallbackStoreTypes = [
  {
    value: "kin_mousse_shop",
    label: `${SHOP} ${MAIN_COMPANY_SHORT}`,
  },
  {
    value: "maison_djo_shop",
    label: `${SHOP} ${MAIN_DISTRIBUTOR}`,
  },
  {
    value: "relay_shop",
    label: `${SHOP} ${RELAY_DEPOT}`,
  },
]


// =========================================================
// HELPERS
// =========================================================

function getGenericStoreTypeLabel(value) {
  const labels = {
    kin_mousse_shop: `${SHOP} ${MAIN_COMPANY_SHORT}`,
    maison_djo_shop: `${SHOP} ${MAIN_DISTRIBUTOR}`,
    relay_shop: `${SHOP} ${RELAY_DEPOT}`,
  }

  return labels[value] || value || "-"
}


function normalizeStoreTypes(types = []) {
  if (
    !Array.isArray(types) ||
    types.length === 0
  ) {
    return fallbackStoreTypes
  }

  return types.map((type) => ({
    ...type,
    label: getGenericStoreTypeLabel(type.value),
  }))
}


function getStoreTypeLabel(
  value,
  storeTypes = []
) {
  const found = storeTypes.find(
    (item) => item.value === value
  )

  return (
    found?.label ||
    getGenericStoreTypeLabel(value)
  )
}


function inferStoreTypeFromOrganization(
  organization
) {
  if (!organization) {
    return "kin_mousse_shop"
  }

  if (
    organization.organization_type ===
    "distributor"
  ) {
    return "maison_djo_shop"
  }

  if (
    organization.organization_type ===
    "relay_depot"
  ) {
    return "relay_shop"
  }

  return "kin_mousse_shop"
}


function getApiErrorMessage(
  error,
  fallback
) {
  const detail =
    error?.response?.data?.detail

  if (
    typeof detail === "string"
  ) {
    return detail
  }

  if (
    Array.isArray(detail)
  ) {
    return detail
      .map(
        (item) =>
          item?.msg ||
          JSON.stringify(item)
      )
      .join(" | ")
  }

  if (
    detail &&
    typeof detail === "object"
  ) {
    return (
      detail.message ||
      JSON.stringify(detail)
    )
  }

  return (
    error?.message ||
    fallback
  )
}


function getStoreRowClass(isActive) {
  return isActive
    ? "store-row store-row--active"
    : "store-row store-row--inactive"
}


// =========================================================
// KPI
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.09)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: { xs: 1.35, sm: 1.5 },
        py: 1.15,
        minWidth: 0,
        borderRadius: 3,
        borderColor: "rgba(148, 163, 184, 0.22)",
        bgcolor: "#ffffff",
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.035)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            borderRadius: 2.5,
            color: accent,
            bgcolor: background,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 0.7,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 19, sm: 21 },
                lineHeight: 1,
                fontWeight: 900,
                color: "#0f172a",
                whiteSpace: "nowrap",
              }}
            >
              {value}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                minWidth: 0,
                fontWeight: 800,
                color: "#334155",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mt: 0.35,
              lineHeight: 1.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// MOBILE CARD
// =========================================================

function StoreMobileCard({
  row,
  storeTypes,
  onEdit,
  onDelete,
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: "rgba(148, 163, 184, 0.22)",
        boxShadow: row.is_active
          ? "inset 4px 0 0 #22c55e, 0 5px 16px rgba(15, 23, 42, 0.025)"
          : "inset 4px 0 0 #94a3b8, 0 5px 16px rgba(15, 23, 42, 0.025)",
      }}
    >
      <CardContent
        sx={{
          p: 1.5,
          "&:last-child": { pb: 1.5 },
        }}
      >
        <Stack spacing={1.2}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  overflowWrap: "anywhere",
                }}
              >
                {row.name || "-"}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                {row.code || "-"}
              </Typography>
            </Box>

            <Chip
              size="small"
              color={
                row.is_active
                  ? "success"
                  : "default"
              }
              label={
                row.is_active
                  ? "Actif"
                  : "Inactif"
              }
            />
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 1,
              borderRadius: 2.5,
              bgcolor: "#fbfdff",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              Organisation
            </Typography>

            <Typography
              variant="body2"
              fontWeight={800}
              color="#334155"
            >
              {row.organization_name || "-"}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
            >
              {row.organization_code || "-"}
            </Typography>
          </Paper>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.7,
            }}
          >
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              label={getStoreTypeLabel(
                row.store_type,
                storeTypes
              )}
            />

            {row.city && (
              <Chip
                size="small"
                variant="outlined"
                label={row.city}
              />
            )}
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
            >
              Adresse
            </Typography>

            <Typography
              variant="body2"
              fontWeight={700}
              color="#475569"
              sx={{
                overflowWrap: "anywhere",
              }}
            >
              {row.address || "-"}
            </Typography>
          </Box>

          {row.parent_store_name ? (
            <Typography
              variant="caption"
              color="text.secondary"
            >
              Parent : {row.parent_store_name}
            </Typography>
          ) : null}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                "repeat(2, minmax(0, 1fr))",
              gap: 0.7,
            }}
          >
            <Button
              variant="contained"
              size="small"
              disableElevation
              onClick={() => onEdit(row.id)}
            >
              Modifier
            </Button>

            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => onDelete(row.id)}
            >
              Supprimer
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}


// =========================================================
// PAGE
// =========================================================

function StoresPage() {
  const theme = useTheme()

  const isMdDown = useMediaQuery(
    theme.breakpoints.down("md")
  )

  const isSmDown = useMediaQuery(
    theme.breakpoints.down("sm")
  )

  const [stores, setStores] = useState([])
  const [organizations, setOrganizations] = useState([])
  const [parentStores, setParentStores] = useState([])
  const [companies, setCompanies] = useState([])
  const [storeTypes, setStoreTypes] = useState(
    fallbackStoreTypes
  )

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [open, setOpen] = useState(false)
  const [selectedStore, setSelectedStore] = useState(null)

  const [formData, setFormData] = useState({
    ...defaultFormData,
  })

  const [searchTerm, setSearchTerm] = useState("")
  const [selectedType, setSelectedType] = useState("")
  const [selectedCity, setSelectedCity] = useState("")
  const [selectedOrganization, setSelectedOrganization] = useState("")

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: ROWS_PER_PAGE,
  })

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message: String(message || ""),
    })
  }


  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }))
  }


  const loadStores = async () => {
    try {
      setLoading(true)

      const data = await getStores()

      setStores(
        Array.isArray(data)
          ? data
          : []
      )
    } catch (error) {
      console.error(
        "Error loading stores:",
        error
      )

      showSnackbar(
        getApiErrorMessage(
          error,
          `Impossible de charger les ${SHOPS.toLowerCase()}.`
        ),
        "error"
      )
    } finally {
      setLoading(false)
    }
  }


  const loadFormData = async () => {
    try {
      const data = await getStoreFormData()

      setOrganizations(
        Array.isArray(data?.organizations)
          ? data.organizations
          : []
      )

      setParentStores(
        Array.isArray(data?.parent_stores)
          ? data.parent_stores
          : []
      )

      setCompanies(
        Array.isArray(data?.companies)
          ? data.companies
          : []
      )

      setStoreTypes(
        normalizeStoreTypes(data?.store_types)
      )
    } catch (error) {
      console.error(
        "Error loading store form data:",
        error
      )

      showSnackbar(
        getApiErrorMessage(
          error,
          `Impossible de charger les données du formulaire ${SHOP.toLowerCase()}.`
        ),
        "error"
      )
    }
  }


  useEffect(() => {
    loadStores()
    loadFormData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const resetForm = () => {
    setSelectedStore(null)

    setFormData({
      ...defaultFormData,
    })
  }


  const generateCodeForForm = async (
    organizationId,
    storeType
  ) => {
    try {
      if (!organizationId) return ""

      const response = await getNextStoreCode({
        organization_id: organizationId,
        store_type: storeType,
      })

      return response?.code || ""
    } catch (error) {
      console.error(error)

      showSnackbar(
        getApiErrorMessage(
          error,
          `Impossible de générer le code ${SHOP}.`
        ),
        "error"
      )

      return ""
    }
  }


  const openAddDialog = async () => {
    setSelectedStore(null)

    let nextForm = {
      ...defaultFormData,
    }

    if (organizations.length === 1) {
      const organization = organizations[0]

      const inferredStoreType =
        inferStoreTypeFromOrganization(
          organization
        )

      const nextCode = await generateCodeForForm(
        organization.id,
        inferredStoreType
      )

      nextForm = {
        ...nextForm,
        organization_id: organization.id,
        store_type: inferredStoreType,
        code: nextCode,
      }
    }

    setFormData(nextForm)
    setOpen(true)
  }


  const openEditDialog = async (storeId) => {
    try {
      const store = await getStoreById(storeId)

      setSelectedStore(store)

      setFormData({
        code:
          store.code || "",

        name:
          store.name || "",

        organization_id:
          store.organization_id || "",

        parent_store_id:
          store.parent_store_id || "",

        store_type:
          store.store_type || "kin_mousse_shop",

        address:
          store.address || "",

        city:
          store.city || "Kinshasa",

        province:
          store.province || "Kinshasa",

        is_active:
          typeof store.is_active === "boolean"
            ? store.is_active
            : true,

        company_id:
          store.company_id || 1,
      })

      setOpen(true)
    } catch (error) {
      console.error(error)

      showSnackbar(
        getApiErrorMessage(
          error,
          `Impossible de charger le détail du ${SHOP}.`
        ),
        "error"
      )
    }
  }


  const handleClose = () => {
    if (saving) return

    setOpen(false)
    resetForm()
  }


  const handleOrganizationChange = async (event) => {
    const value = event.target.value

    const organization = organizations.find(
      (item) =>
        String(item.id) === String(value)
    )

    const inferredStoreType =
      inferStoreTypeFromOrganization(
        organization
      )

    let nextCode = formData.code

    if (!selectedStore) {
      nextCode = await generateCodeForForm(
        value,
        inferredStoreType
      )
    }

    setFormData((prev) => ({
      ...prev,

      organization_id:
        value === ""
          ? ""
          : Number(value),

      store_type:
        inferredStoreType,

      parent_store_id: "",

      code:
        nextCode,
    }))
  }


  const handleStoreTypeChange = async (event) => {
    const value = event.target.value

    let nextCode = formData.code

    if (
      !selectedStore &&
      formData.organization_id
    ) {
      nextCode = await generateCodeForForm(
        formData.organization_id,
        value
      )
    }

    setFormData((prev) => ({
      ...prev,
      store_type: value,
      code: nextCode,
    }))
  }


  const handleChange = (event) => {
    const { name, value } = event.target

    setFormData((prev) => ({
      ...prev,

      [name]:
        name === "is_active"
          ? value === "true" || value === true
          : [
              "company_id",
              "organization_id",
              "parent_store_id",
            ].includes(name)
          ? value === ""
            ? ""
            : Number(value)
          : value,
    }))
  }


  const validateForm = () => {
    if (!formData.organization_id) {
      showSnackbar(
        "L'organisation est obligatoire.",
        "warning"
      )
      return false
    }

    if (!formData.name?.trim()) {
      showSnackbar(
        `Le nom du ${SHOP.toLowerCase()} est obligatoire.`,
        "warning"
      )
      return false
    }

    if (!formData.store_type) {
      showSnackbar(
        `Le type de ${SHOP.toLowerCase()} est obligatoire.`,
        "warning"
      )
      return false
    }

    if (
      selectedStore?.id &&
      Number(formData.parent_store_id) ===
        Number(selectedStore.id)
    ) {
      showSnackbar(
        `Un ${SHOP.toLowerCase()} ne peut pas être son propre parent.`,
        "warning"
      )
      return false
    }

    return true
  }


  const handleSave = async () => {
    if (!validateForm()) return

    const payload = {
      code:
        formData.code?.trim().toUpperCase() ||
        "AUTO",

      name:
        formData.name.trim(),

      organization_id:
        Number(formData.organization_id),

      parent_store_id:
        formData.parent_store_id
          ? Number(formData.parent_store_id)
          : null,

      store_type:
        formData.store_type,

      address:
        formData.address?.trim() || null,

      city:
        formData.city?.trim() || null,

      province:
        formData.province?.trim() || null,

      is_active:
        Boolean(formData.is_active),

      company_id:
        Number(formData.company_id || 1),
    }

    try {
      setSaving(true)

      if (selectedStore?.id) {
        await updateStore(
          selectedStore.id,
          payload
        )

        showSnackbar(
          `${SHOP} modifié avec succès.`
        )
      } else {
        await createStore(payload)

        showSnackbar(
          `${SHOP} créé avec succès.`
        )
      }

      /*
       * Fermeture directe :
       * handleClose() bloque lorsque saving === true.
       */
      setOpen(false)
      resetForm()

      await loadStores()
      await loadFormData()
    } catch (error) {
      console.error(error)

      showSnackbar(
        getApiErrorMessage(
          error,
          `Impossible d'enregistrer le ${SHOP}.`
        ),
        "error"
      )
    } finally {
      setSaving(false)
    }
  }


  const handleDelete = async (storeId) => {
    const confirmed = window.confirm(
      `Voulez-vous vraiment supprimer ce ${SHOP.toLowerCase()} ?`
    )

    if (!confirmed) return

    try {
      await deleteStore(storeId)

      showSnackbar(
        `${SHOP} supprimé avec succès.`
      )

      await loadStores()
      await loadFormData()
    } catch (error) {
      console.error(error)

      showSnackbar(
        getApiErrorMessage(
          error,
          `Impossible de supprimer le ${SHOP}.`
        ),
        "error"
      )
    }
  }


  const uniqueCities = useMemo(() => {
    return [
      ...new Set(
        stores
          .map((store) => store.city)
          .filter(Boolean)
      ),
    ].sort()
  }, [stores])


  const filteredStores = useMemo(() => {
    return stores.filter((store) => {
      const query = searchTerm
        .trim()
        .toLowerCase()

      const matchesSearch =
        !query ||
        [
          store.code,
          store.name,
          store.address,
          store.organization_name,
          store.organization_code,
          store.city,
          store.province,
          store.parent_store_name,
          getStoreTypeLabel(
            store.store_type,
            storeTypes
          ),
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(query)
        )

      const matchesType =
        !selectedType ||
        store.store_type === selectedType

      const matchesCity =
        !selectedCity ||
        store.city === selectedCity

      const matchesOrganization =
        !selectedOrganization ||
        String(store.organization_id) ===
          String(selectedOrganization)

      return (
        matchesSearch &&
        matchesType &&
        matchesCity &&
        matchesOrganization
      )
    })
  }, [
    stores,
    searchTerm,
    selectedType,
    selectedCity,
    selectedOrganization,
    storeTypes,
  ])


  const summary = useMemo(() => {
    const active = filteredStores.filter(
      (store) => Boolean(store.is_active)
    ).length

    const inactive =
      filteredStores.length - active

    const organizationCount = new Set(
      filteredStores
        .map((store) => store.organization_id)
        .filter(Boolean)
    ).size

    return {
      total: filteredStores.length,
      active,
      inactive,
      organizationCount,
    }
  }, [filteredStores])


  useEffect(() => {
    setPaginationModel((previous) => ({
      ...previous,
      page: 0,
      pageSize: ROWS_PER_PAGE,
    }))
  }, [
    searchTerm,
    selectedType,
    selectedCity,
    selectedOrganization,
  ])


  const paginatedMobileRows = useMemo(() => {
    const start =
      paginationModel.page * ROWS_PER_PAGE

    return filteredStores.slice(
      start,
      start + ROWS_PER_PAGE
    )
  }, [
    filteredStores,
    paginationModel.page,
  ])


  useEffect(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(
        filteredStores.length / ROWS_PER_PAGE
      )
    )

    if (paginationModel.page >= totalPages) {
      setPaginationModel((previous) => ({
        ...previous,
        page: totalPages - 1,
        pageSize: ROWS_PER_PAGE,
      }))
    }
  }, [
    filteredStores.length,
    paginationModel.page,
  ])


  const resetFilters = () => {
    setSearchTerm("")
    setSelectedType("")
    setSelectedCity("")
    setSelectedOrganization("")

    setPaginationModel({
      page: 0,
      pageSize: ROWS_PER_PAGE,
    })
  }


  const parentOptions = useMemo(() => {
    return parentStores.filter((store) => {
      if (
        selectedStore?.id &&
        Number(store.id) === Number(selectedStore.id)
      ) {
        return false
      }

      if (!formData.organization_id) {
        return true
      }

      return (
        String(store.organization_id) ===
        String(formData.organization_id)
      )
    })
  }, [
    parentStores,
    selectedStore,
    formData.organization_id,
  ])


  const availableStoreTypes = useMemo(() => {
    const organization = organizations.find(
      (item) =>
        String(item.id) ===
        String(formData.organization_id)
    )

    if (!organization) {
      return storeTypes
    }

    if (
      organization.organization_type ===
      "distributor"
    ) {
      return storeTypes.filter(
        (item) =>
          item.value === "maison_djo_shop"
      )
    }

    if (
      organization.organization_type ===
      "relay_depot"
    ) {
      return storeTypes.filter(
        (item) =>
          item.value === "relay_shop"
      )
    }

    return storeTypes.filter(
      (item) =>
        item.value === "kin_mousse_shop"
    )
  }, [
    organizations,
    formData.organization_id,
    storeTypes,
  ])


  const columns = useMemo(
    () => [
      {
        field: "store",
        headerName: SHOP,
        minWidth: 215,
        flex: 1.15,
        sortable: false,
        renderCell: (params) => (
          <Stack
            spacing={0.16}
            justifyContent="center"
            sx={{
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Typography
              sx={{
                color: "#0f172a",
                fontSize: "0.86rem",
                fontWeight: 900,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={params.row.name || ""}
            >
              {params.row.name || "-"}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700 }}
            >
              {params.row.code || "-"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "organization",
        headerName: "Organisation",
        minWidth: 220,
        flex: 1.2,
        sortable: false,
        renderCell: (params) => (
          <Stack
            spacing={0.14}
            justifyContent="center"
            sx={{
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Typography
              sx={{
                color: "#334155",
                fontSize: "0.83rem",
                fontWeight: 900,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.row.organization_name || "-"}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
            >
              {params.row.organization_code || "-"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "store_type",
        headerName: "Type",
        minWidth: 190,
        flex: 1.05,
        renderCell: (params) => (
          <Chip
            label={getStoreTypeLabel(
              params.value,
              storeTypes
            )}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              maxWidth: 180,
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        ),
      },
      {
        field: "location",
        headerName: "Localisation",
        minWidth: 220,
        flex: 1.15,
        sortable: false,
        renderCell: (params) => (
          <Stack
            spacing={0.14}
            justifyContent="center"
            sx={{
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Typography
              sx={{
                color: "#334155",
                fontSize: "0.83rem",
                fontWeight: 800,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {[params.row.city, params.row.province]
                .filter(Boolean)
                .join(" • ") || "-"}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={params.row.address || ""}
            >
              {params.row.address || "Adresse non renseignée"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "parent_store_name",
        headerName: "Parent",
        minWidth: 140,
        flex: 0.8,
        renderCell: (params) => (
          <Typography
            sx={{
              color: "#475569",
              fontSize: "0.82rem",
              fontWeight: 700,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: "is_active",
        headerName: "Statut",
        width: 105,
        renderCell: (params) => (
          <Chip
            size="small"
            color={
              params.value
                ? "success"
                : "default"
            }
            label={
              params.value
                ? "Actif"
                : "Inactif"
            }
          />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 190,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.55}>
            <Button
              variant="contained"
              size="small"
              disableElevation
              onClick={() =>
                openEditDialog(params.row.id)
              }
            >
              Modifier
            </Button>

            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() =>
                handleDelete(params.row.id)
              }
            >
              Supprimer
            </Button>
          </Stack>
        ),
      },
    ],
    [storeTypes]
  )


  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          pb: { xs: 2, md: 4 },

          "& .MuiButton-root": {
            minHeight: 40,
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 800,
            boxShadow: "none",
          },

          "& .MuiOutlinedInput-root": {
            borderRadius: 2.5,
            bgcolor: "#ffffff",
          },

          "& .MuiChip-root": {
            fontWeight: 800,
          },

          "& .MuiPaper-root": {
            backgroundImage: "none",
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "minmax(0, 1fr) auto",
            },
            gap: 1.5,
            alignItems: "start",
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                color: "#0f172a",
                lineHeight: 1.12,
                letterSpacing: "-0.025em",
                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
              }}
            >
              {SHOPS} / Points de vente
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.4,
                lineHeight: 1.5,
                maxWidth: 900,
              }}
            >
              Création et gestion des {SHOPS.toLowerCase()} de{" "}
              {MAIN_COMPANY}, du {MAIN_DISTRIBUTOR.toLowerCase()} et des{" "}
              {RELAY_DEPOTS.toLowerCase()}. Les{" "}
              {FIELD_AGENTS.toLowerCase()} sont créés dans Utilisateurs.
            </Typography>
          </Box>

          <Button
            variant="contained"
            disableElevation
            onClick={openAddDialog}
            sx={{
              minWidth: { xs: "100%", sm: 180 },
              justifySelf: { xs: "stretch", sm: "end" },
            }}
          >
            + Nouveau {SHOP.toLowerCase()}
          </Button>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
            },
            gap: 1.1,
            mb: 1.6,
          }}
        >
          <MetricCard
            label={SHOPS}
            value={summary.total}
            caption="Points de vente affichés"
            accent="#2563eb"
            background="rgba(37, 99, 235, 0.09)"
            icon={<StorefrontRoundedIcon fontSize="small" />}
          />

          <MetricCard
            label="Actifs"
            value={summary.active}
            caption={`${SHOPS} opérationnels`}
            accent="#16a34a"
            background="rgba(22, 163, 74, 0.09)"
            icon={<CheckCircleRoundedIcon fontSize="small" />}
          />

          <MetricCard
            label="Inactifs"
            value={summary.inactive}
            caption={`${SHOPS} désactivés`}
            accent="#64748b"
            background="rgba(100, 116, 139, 0.09)"
            icon={<BlockRoundedIcon fontSize="small" />}
          />

          <MetricCard
            label="Organisations"
            value={summary.organizationCount}
            caption="Structures représentées"
            accent="#7c3aed"
            background="rgba(124, 58, 237, 0.09)"
            icon={<CorporateFareRoundedIcon fontSize="small" />}
          />
        </Box>

        <Paper
          variant="outlined"
          sx={{
            p: { xs: 1.15, sm: 1.25 },
            mb: 1.6,
            borderRadius: 3,
            borderColor: "rgba(148, 163, 184, 0.22)",
            boxShadow: "0 5px 16px rgba(15, 23, 42, 0.025)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg:
                  "minmax(0, 1fr) 230px 210px 180px 135px",
              },
              gap: 1,
              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Recherche"
              placeholder="Code, nom, adresse ou organisation..."
              value={searchTerm}
              onChange={(event) =>
                setSearchTerm(event.target.value)
              }
            />

            <TextField
              select
              fullWidth
              size="small"
              label="Organisation"
              value={selectedOrganization}
              onChange={(event) =>
                setSelectedOrganization(event.target.value)
              }
            >
              <MenuItem value="">Toutes</MenuItem>

              {organizations.map((organization) => (
                <MenuItem
                  key={organization.id}
                  value={organization.id}
                >
                  {organization.code} - {organization.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              size="small"
              label="Type"
              value={selectedType}
              onChange={(event) =>
                setSelectedType(event.target.value)
              }
            >
              <MenuItem value="">Tous</MenuItem>

              {storeTypes.map((type) => (
                <MenuItem
                  key={type.value}
                  value={type.value}
                >
                  {type.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              size="small"
              label="Ville"
              value={selectedCity}
              onChange={(event) =>
                setSelectedCity(event.target.value)
              }
            >
              <MenuItem value="">Toutes</MenuItem>

              {uniqueCities.map((city) => (
                <MenuItem
                  key={city}
                  value={city}
                >
                  {city}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="outlined"
              onClick={resetFilters}
              sx={{
                width: "100%",
                minWidth: 0,
                whiteSpace: "nowrap",
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3.5,
            overflow: "hidden",
            borderColor: "rgba(148, 163, 184, 0.22)",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >
          <Box
            sx={{
              px: { xs: 1.4, sm: 1.8 },
              py: 1.15,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 0.8,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "#ffffff",
            }}
          >
            <Box>
              <Typography fontWeight={900} color="#0f172a">
                Registre des {SHOPS.toLowerCase()}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Organisation, type, localisation, parent et statut
              </Typography>
            </Box>

            <Chip
              size="small"
              variant="outlined"
              label={`${filteredStores.length} ${SHOP.toLowerCase()}${
                filteredStores.length > 1 ? "s" : ""
              }`}
            />
          </Box>

          {!isMdDown ? (
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                overflowX: "auto",
                bgcolor: "#ffffff",
              }}
            >
              <Box sx={{ minWidth: 1120 }}>
                <DataGrid
                  autoHeight
                  rows={filteredStores}
                  columns={columns}
                  loading={loading}
                  disableRowSelectionOnClick
                  disableColumnSelector
                  pagination
                  paginationMode="client"
                  paginationModel={paginationModel}
                  onPaginationModelChange={(model) =>
                    setPaginationModel({
                      page: model.page,
                      pageSize: ROWS_PER_PAGE,
                    })
                  }
                  pageSizeOptions={[ROWS_PER_PAGE]}
                  rowHeight={72}
                  columnHeaderHeight={50}
                  getRowClassName={(params) =>
                    getStoreRowClass(params.row.is_active)
                  }
                  localeText={{
                    noRowsLabel: `Aucun ${SHOP.toLowerCase()}`,
                    noResultsOverlayLabel: "Aucun résultat",
                    MuiTablePagination: {
                      labelRowsPerPage: "",
                      labelDisplayedRows: ({
                        from,
                        to,
                        count,
                      }) => `${from}–${to} sur ${count}`,
                    },
                  }}
                  sx={{
                    border: 0,
                    width: "100%",
                    color: "#334155",
                    fontSize: "0.83rem",
                    bgcolor: "#ffffff",

                    "& .MuiDataGrid-columnHeaders": {
                      bgcolor: "#f8fafc",
                      borderBottom: "1px solid #dfe7f1",
                    },

                    "& .MuiDataGrid-columnHeader": {
                      bgcolor: "#f8fafc",
                      px: 1.25,
                      borderRight:
                        "1px solid rgba(226, 232, 240, 0.60)",
                    },

                    "& .MuiDataGrid-columnHeaderTitle": {
                      color: "#334155",
                      fontSize: "0.76rem",
                      fontWeight: 900,
                      letterSpacing: "0.025em",
                    },

                    "& .MuiDataGrid-cell": {
                      px: 1.25,
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      overflow: "hidden",
                      borderBottom: "1px solid #edf2f7",
                      borderRight:
                        "1px solid rgba(241, 245, 249, 0.85)",
                    },

                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                      {
                        outline: "none",
                      },

                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                      {
                        outline: "none",
                      },

                    "& .store-row": {
                      transition:
                        "background-color 0.16s ease, box-shadow 0.16s ease",
                    },

                    "& .store-row--active": {
                      boxShadow: "inset 4px 0 0 #22c55e",
                      bgcolor: "rgba(240, 253, 244, 0.28)",
                    },

                    "& .store-row--inactive": {
                      boxShadow: "inset 4px 0 0 #94a3b8",
                      bgcolor: "rgba(248, 250, 252, 0.70)",
                    },

                    "& .store-row:hover": {
                      bgcolor: "#f8fbff !important",
                      boxShadow:
                        "inset 4px 0 0 #2563eb, 0 4px 14px rgba(15, 23, 42, 0.045)",
                    },

                    "& .MuiDataGrid-footerContainer": {
                      minHeight: 52,
                      bgcolor: "#fbfdff",
                      borderTop: "1px solid #dfe7f1",
                    },

                    "& .MuiTablePagination-selectLabel": {
                      display: "none",
                    },

                    "& .MuiTablePagination-input": {
                      display: "none",
                    },
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                p: { xs: 1.1, sm: 1.25 },
                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box sx={{ py: 5, textAlign: "center" }}>
                  <Typography color="text.secondary">
                    Chargement des {SHOPS.toLowerCase()}...
                  </Typography>
                </Box>
              ) : paginatedMobileRows.length === 0 ? (
                <Box sx={{ py: 5, textAlign: "center" }}>
                  <Typography color="text.secondary">
                    Aucun {SHOP.toLowerCase()} trouvé.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {paginatedMobileRows.map((row) => (
                    <StoreMobileCard
                      key={row.id}
                      row={row}
                      storeTypes={storeTypes}
                      onEdit={openEditDialog}
                      onDelete={handleDelete}
                    />
                  ))}
                </Stack>
              )}

              <TablePagination
                component="div"
                count={filteredStores.length}
                page={paginationModel.page}
                onPageChange={(_event, newPage) =>
                  setPaginationModel((previous) => ({
                    ...previous,
                    page: newPage,
                    pageSize: ROWS_PER_PAGE,
                  }))
                }
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
                labelRowsPerPage=""
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} sur ${count}`
                }
                sx={{
                  mt: 1,
                  bgcolor: "#ffffff",
                  borderRadius: 2.5,

                  "& .MuiTablePagination-selectLabel": {
                    display: "none",
                  },

                  "& .MuiTablePagination-input": {
                    display: "none",
                  },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        fullScreen={isSmDown}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 4 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: "100dvh", sm: "92dvh" },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.3 }}>
          <Typography fontWeight={900} fontSize={20} color="#0f172a">
            {selectedStore
              ? `Modifier le ${SHOP.toLowerCase()}`
              : `Créer un nouveau ${SHOP.toLowerCase()}`}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.3 }}
          >
            Le code est généré automatiquement par RLSys et le type est
            déduit selon l’organisation sélectionnée.
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Alert severity="info" variant="outlined">
              Le code du {SHOP.toLowerCase()} est généré automatiquement par
              RLSys. Le type est déduit selon l’organisation sélectionnée.
            </Alert>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 3,
                borderColor: "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{ mb: 1.2 }}
              >
                Organisation et type
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                <TextField
                  select
                  size="small"
                  label="Organisation"
                  name="organization_id"
                  value={formData.organization_id}
                  onChange={handleOrganizationChange}
                  fullWidth
                  required
                  disabled={saving}
                >
                  {organizations.map((organization) => (
                    <MenuItem
                      key={organization.id}
                      value={organization.id}
                    >
                      {organization.code} - {organization.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label={`Type de ${SHOP.toLowerCase()}`}
                  name="store_type"
                  value={formData.store_type}
                  onChange={handleStoreTypeChange}
                  fullWidth
                  required
                  disabled={saving}
                >
                  {availableStoreTypes.map((type) => (
                    <MenuItem
                      key={type.value}
                      value={type.value}
                    >
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 3,
                bgcolor: "#f8fafc",
                borderColor: "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{ mb: 1.2 }}
              >
                Identification
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                <TextField
                  size="small"
                  label="Code"
                  name="code"
                  value={
                    formData.code ||
                    "Généré automatiquement"
                  }
                  fullWidth
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText={
                    selectedStore
                      ? `Le code du ${SHOP.toLowerCase()} ne se modifie pas.`
                      : "Le code sera généré automatiquement. Exemple : SHOP-001."
                  }
                />

                <TextField
                  size="small"
                  label={`Nom du ${SHOP.toLowerCase()}`}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  fullWidth
                  required
                  disabled={saving}
                />
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 3,
                borderColor: "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{ mb: 1.2 }}
              >
                Localisation
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                  mb: 1.5,
                }}
              >
                <TextField
                  size="small"
                  label="Ville"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  fullWidth
                  disabled={saving}
                />

                <TextField
                  size="small"
                  label="Province"
                  name="province"
                  value={formData.province}
                  onChange={handleChange}
                  fullWidth
                  disabled={saving}
                />
              </Box>

              <TextField
                label="Adresse"
                name="address"
                value={formData.address}
                onChange={handleChange}
                fullWidth
                multiline
                minRows={2}
                disabled={saving}
              />
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 3,
                bgcolor: "#f8fafc",
                borderColor: "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{ mb: 1.2 }}
              >
                Structure et statut
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                <TextField
                  select
                  size="small"
                  label={`${SHOP} parent`}
                  name="parent_store_id"
                  value={formData.parent_store_id}
                  onChange={handleChange}
                  fullWidth
                  disabled={saving}
                  helperText={`Optionnel. À utiliser seulement si un ${SHOP.toLowerCase()} dépend d’un autre.`}
                >
                  <MenuItem value="">Aucun</MenuItem>

                  {parentOptions.map((store) => (
                    <MenuItem
                      key={store.id}
                      value={store.id}
                    >
                      {store.code} - {store.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Entreprise"
                  name="company_id"
                  value={formData.company_id}
                  onChange={handleChange}
                  fullWidth
                  disabled={saving}
                >
                  {companies.map((company) => (
                    <MenuItem
                      key={company.id}
                      value={company.id}
                    >
                      {company.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Statut"
                  name="is_active"
                  value={String(formData.is_active)}
                  onChange={handleChange}
                  fullWidth
                  disabled={saving}
                  sx={{
                    gridColumn: {
                      xs: "auto",
                      md: "1 / -1",
                    },
                  }}
                >
                  <MenuItem value="true">
                    Actif
                  </MenuItem>

                  <MenuItem value="false">
                    Inactif
                  </MenuItem>
                </TextField>
              </Box>
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            gap: 1,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            onClick={handleClose}
            disabled={saving}
          >
            Annuler
          </Button>

          <Button
            variant="contained"
            disableElevation
            onClick={handleSave}
            disabled={saving}
          >
            {saving
              ? "Enregistrement..."
              : selectedStore
              ? "Modifier"
              : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}

export default StoresPage
